/**
 * MaxDiff Survey Utility Functions
 *
 * This module provides utilities for generating balanced MaxDiff survey sets
 * using a heuristic algorithm that balances both item and pair frequencies.
 */

// --- Seeding & Shuffling Helpers ---

/**
 * Creates a simple Linear Congruential Generator (LCG) for seeded
 * pseudo-random number generation. This is used to make the shuffles
 * deterministic and reproducible.
 * @param seed The initial seed value.
 * @returns A function that returns a new random number (0-1) each time it's called.
 */
function createPRNG(seed: number) {
  // LCG parameters (simple and common ones)
  const a = 1664525;
  const c = 1013904223;
  const m = 2 ** 32; // 4294967296

  return function () {
    seed = (a * seed + c) % m;
    return seed / m;
  };
}

/**
 * A simple string hashing function (djb2) to convert any
 * string (like a userId) into a numeric seed.
 * @param str The string to hash.
 * @returns A 32-bit integer.
 */
function stringToSeed(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0; // Ensure positive 32-bit integer
}

/**
 * Performs a Fisher-Yates shuffle on a *copy* of an array
 * using a deterministic, seeded PRNG.
 * @param array The array to shuffle.
 * @param seed The numeric seed to use for the shuffle.
 * @returns A new, shuffled array.
 */
function seededShuffle<T>(array: T[], seed: number): T[] {
  const prng = createPRNG(seed);
  const shuffled = [...array]; // Work on a copy
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(prng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// --- Canonical Pair Helper ---

/**
 * Creates a canonical, sorted key for a pair of items.
 * This ensures that ("Item A", "Item B") and ("Item B", "Item A")
 * are treated as the same pair.
 * @param item1 - The first item string.
 * @param item2 - The second item string.
 * @returns A sorted, delimited string (e.g., "Item A||Item B").
 */
function getPairKey(item1: string, item2: string): string {
  return [item1, item2].sort().join('||');
}

// --- Main Generation & Administration Function ---

/**
 * Generates and prepares approximately balanced sets for a *single respondent*
 * using their `userId` for reproducible randomization.
 *
 * This function uses a heuristic approach to achieve three goals:
 * 1.  **Approximate Item Balance:** Tries to make each item appear a
 * roughly equal number of times (`targetRepeats`).
 * 2.  **Approximate Pair Balance:** Tries to make each *pair* of items
 * appear together a roughly equal number of times.
 * 3.  **Reproducible Randomization:** Uses the `userId` to seed all
 * randomization for this specific respondent (both set order
 * and item order within sets).
 *
 * NOTE: This is a heuristic optimization and *not* a pure mathematical
 * BIBD solver. A perfect BIBD may not exist for all input combinations.
 *
 * @param items - An array of item strings to be included in the sets.
 * @param userId - A unique identifier for the respondent (e.g., "user-12345").
 * @param itemsPerSet - The number of items to show in each set (default: 4).
 * @param targetRepeats - The desired number of times each item should appear (default: 4).
 * @param setsPerUser - The number of sets to randomly select and
 * show this user. If not provided, all generated sets will be shown.
 * @returns An array of sets (string[][]), fully randomized for a single user.
 */
export function generateAndPrepareSetsForUser(
  items: string[],
  userId: string,
  itemsPerSet: number = 4,
  targetRepeats: number = 4,
  setsPerUser?: number,
): string[][] {
  // Convert the string userId into a numeric seed for all admin randomization
  const userSeed = stringToSeed(userId);

  if (items.length <= itemsPerSet) {
    // If not enough items, just return the single set, but shuffle
    // it based on the user's ID for administration.
    const shuffledItems = seededShuffle([...items], userSeed);
    return [shuffledItems];
  }

  // This `masterDesign` will be created deterministically
  const masterDesign: string[][] = [];
  const itemAppearanceCount: Record<string, number> = {};
  const pairAppearanceCount: Record<string, number> = {};

  // Initialize counters
  items.forEach((item) => {
    itemAppearanceCount[item] = 0;
  });

  // Initialize pair counters
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const key = getPairKey(items[i], items[j]);
      pairAppearanceCount[key] = 0;
    }
  }

  // Calculate the total number of sets (tasks) to generate
  const totalSets = Math.ceil((items.length * targetRepeats) / itemsPerSet);

  // Define weights for scoring. We care slightly more about balancing
  // pairs, as this is the harder problem.
  const itemWeight = 1.0;
  const pairWeight = 1.5;

  for (let setIndex = 0; setIndex < totalSets; setIndex++) {
    const currentSet: string[] = [];
    let availableItems = [...items];

    // --- 1. Build the Set with Balance in Mind ---

    // Pick the first item: Start with an item that has appeared least often.
    // We rotate the starting pick to ensure variation.
    availableItems.sort((a, b) => itemAppearanceCount[a] - itemAppearanceCount[b]);
    const firstItemIndex = 0;
    const firstItem = availableItems.splice(firstItemIndex, 1)[0];
    currentSet.push(firstItem);

    // Pick the remaining items for the set
    while (currentSet.length < itemsPerSet && availableItems.length > 0) {
      // Score all remaining available items
      const scoredItems = availableItems.map((item) => {
        // Score from item appearances (how many times has this item shown?)
        const itemScore = itemAppearanceCount[item] * itemWeight;

        // Score from pair appearances (how many times has this item
        // shown with items *already in our current set*?)
        let pairScore = 0;
        currentSet.forEach((existingItem) => {
          const key = getPairKey(item, existingItem);
          pairScore += pairAppearanceCount[key] || 0;
        });

        // Average the pair score
        const avgPairScore =
          (pairScore / currentSet.length) * pairWeight;

        return { item, score: itemScore + avgPairScore };
      });

      // Find the best item (lowest score)
      scoredItems.sort((a, b) => a.score - b.score);
      const bestItem = scoredItems[0].item;

      // Add best item to set and remove from available
      currentSet.push(bestItem);
      availableItems = availableItems.filter((item) => item !== bestItem);
    }

    // --- 2. Update Appearance Counters ---
    currentSet.forEach((item) => {
      itemAppearanceCount[item]++;
    });

    // Update pair counts
    for (let i = 0; i < currentSet.length; i++) {
      for (let j = i + 1; j < currentSet.length; j++) {
        const key = getPairKey(currentSet[i], currentSet[j]);
        pairAppearanceCount[key] = (pairAppearanceCount[key] || 0) + 1;
      }
    }

    // --- 3. Randomize Order Within the Set (for Master Design) ---

    // Use a *seeded* PRNG for deterministic, reproducible shuffles.
    // This creates the master design.
    const internalSeed = setIndex * 12345; // Internal, non-user seed
    const deterministicRandom = createPRNG(internalSeed);

    // Standard Fisher-Yates Shuffle Algorithm
    for (let i = currentSet.length - 1; i > 0; i--) {
      const j = Math.floor(deterministicRandom() * (i + 1));
      [currentSet[i], currentSet[j]] = [currentSet[j], currentSet[i]];
    }

    masterDesign.push(currentSet);
  }

  // --- 4. Survey Administration Randomization (Seeded by UserID) ---
  // Now, we take the deterministically-generated masterDesign and
  // randomize it for this specific user.

  // 1. Perform Set/Block Randomization (using userSeed)
  const shuffledSets = seededShuffle(masterDesign, userSeed);

  // 2. Select a subset of sets if setsPerUser is specified
  const userSets = setsPerUser
    ? shuffledSets.slice(0, setsPerUser)
    : shuffledSets;

  // 3. Perform Item-in-Set Randomization (using userSeed + index)
  // We re-shuffle each set for this specific user
  const userReadySets = userSets.map((set, index) => {
    // Combine userSeed and set index to ensure each set's shuffle
    // is unique but still deterministic for this user.
    return seededShuffle(set, userSeed + index);
  });

  return userReadySets;
}

/**
 * MaxDiff Design Evaluation Utilities
 *
 * This module provides functions to evaluate the quality and balance
 * of a generated MaxDiff design.
 */

/**
 * Defines the structure of the evaluation report.
 */
export interface MaxDiffEvaluation {
  // --- Overall Stats ---
  /** Total number of sets (tasks) in the design. */
  totalSets: number;
  /** Number of items in the first set. Assumes all sets are equal length. */
  itemsPerSet: number;
  /** Total number of items in the master list. */
  totalItems: number;

  // --- Item Frequency Report ---
  /**
   * A count of how many times each item appeared in the design.
   * `Record<item, count>`
   */
  itemFrequencies: Record<string, number>;

  // --- Pair Frequency Report ---
  /**
   * Contains the raw counts for each pair and summary statistics
   * about the balance of those pairs.
   */
  pairFrequencies: {
    /**
     * A count of how many times each unique pair appeared.
     * `Record<"Item A||Item B", count>`
     */
    counts: Record<string, number>;
    /**
     * Summary statistics about the balance of the pair counts.
     * A low standard deviation indicates a well-balanced (i.e., "efficient") design.
     */
    stats: {
      /** The total number of unique pairs *expected* for this many items. */
      totalExpectedPairs: number;
      /** The number of unique pairs that *actually* appeared in the design. */
      totalObservedPairs: number;
      /** The fewest times any single pair appeared. */
      min: number;
      /** The most times any single pair appeared. */
      max: number;
      /** The average number of appearances per pair. */
      average: number;
      /** The standard deviation of the pair appearance counts. *A low value is good.* */
      stdDev: number;
    };
  };
  /**
   * Informational notes about the design evaluation.
   */
  notes: string[];
  /**
   * The actual sets (design) that was evaluated.
   */
  sets: string[][];
}



/**
 * Evaluates a generated MaxDiff design for balance and frequency.
 *
 * This function checks its own work by reporting on:
 * 1.  **Item Frequencies:** How many times each item appeared.
 * 2.  **Pair Frequencies:** How many times each pair of items appeared together.
 * 3.  **Balance Statistics:** A practical proxy for D-efficiency by showing
 * the min, max, average, and standard deviation of the pair counts.
 *
 * A well-balanced design will have `min` and `max` pair counts
 * that are very close to each other, and a `stdDev` close to 0.
 *
 * @param design - The `string[][]` array of sets to evaluate.
 * @param allItems - The complete master list of all item strings.
 * @returns An `MaxDiffEvaluation` object with the full report.
 */
export function evaluateMaxDiffDesign(
  design: string[][],
  allItems: string[],
): MaxDiffEvaluation {
  const itemCounts: Record<string, number> = {};
  const pairCounts: Record<string, number> = {};
  const notes: string[] = [];

  // --- 1. Initialize all counters to 0 ---
  // This ensures we catch items/pairs that appeared 0 times.
  allItems.forEach((item) => {
    itemCounts[item] = 0;
  });

  const totalExpectedPairs = (allItems.length * (allItems.length - 1)) / 2;
  for (let i = 0; i < allItems.length; i++) {
    for (let j = i + 1; j < allItems.length; j++) {
      const key = getPairKey(allItems[i], allItems[j]);
      pairCounts[key] = 0;
    }
  }

  // --- 2. Tally Item and Pair Frequencies from the Design ---
  if (!design || design.length === 0) {
    notes.push('Warning: Design is empty or null.');
    // Return early with initialized (all zero) report
    return {
      totalSets: 0,
      itemsPerSet: 0,
      totalItems: allItems.length,
      itemFrequencies: itemCounts,
      pairFrequencies: {
        counts: pairCounts,
        stats: {
          totalExpectedPairs,
          totalObservedPairs: 0,
          min: 0,
          max: 0,
          average: 0,
          stdDev: 0,
        },
      },
      notes,
    };
  }

  design.forEach((set) => {
    if (!set || set.length === 0) return;

    // Tally items
    set.forEach((item) => {
      if (itemCounts.hasOwnProperty(item)) {
        itemCounts[item]++;
      } else {
        // This item was in a set but not in the master `allItems` list
        itemCounts[`__UNEXPECTED_ITEM: ${item}`] = 1;
      }
    });

    // Tally pairs
    for (let i = 0; i < set.length; i++) {
      for (let j = i + 1; j < set.length; j++) {
        const key = getPairKey(set[i], set[j]);
        if (pairCounts.hasOwnProperty(key)) {
          pairCounts[key]++;
        } else {
          pairCounts[`__UNEXPECTED_PAIR: ${key}`] = 1;
        }
      }
    }
  });

  // --- 3. Calculate Balance Statistics (Proxy for D-Efficiency) ---
  const pairValues = Object.values(pairCounts);
  const totalObservedPairs = pairValues.filter((count) => count > 0).length;

  const sum = pairValues.reduce((a, b) => a + b, 0);
  const average = sum / pairValues.length || 0;
  // Guard the empty-pairValues case (e.g. a single-item list → no pairs) so the report
  // never exposes Infinity / -Infinity from Math.min()/Math.max() on an empty array.
  const min = pairValues.length ? Math.min(...pairValues) : 0;
  const max = pairValues.length ? Math.max(...pairValues) : 0;

  const variance =
    pairValues.reduce((acc, val) => acc + (val - average) ** 2, 0) /
    pairValues.length;
  const stdDev = Math.sqrt(variance || 0);

  if (totalObservedPairs < totalExpectedPairs) {
    notes.push(
      `Warning: ${
        totalExpectedPairs - totalObservedPairs
      } pairs never appeared (count = 0).`,
    );
  }

  notes.push(
    'Pair stats (min, max, stdDev) are a practical proxy for D-efficiency. A low stdDev indicates a well-balanced design.',
  );

  // --- 4. Assemble Final Report ---
  return {
    totalSets: design.length,
    itemsPerSet: design[0]?.length || 0,
    totalItems: allItems.length,
    itemFrequencies: itemCounts,
    pairFrequencies: {
      counts: pairCounts,
      stats: {
        totalExpectedPairs,
        totalObservedPairs,
        min,
        max,
        average: parseFloat(average.toFixed(2)),
        stdDev: parseFloat(stdDev.toFixed(2)),
      },
    },
    notes,
    sets: design,
  };
}